/**
 * SMSRateLimitsPage - Admin-only page to manage tenant SMS rate limits.
 *
 * ## Why Rate Limits Exist
 *
 * These are APPLICATION-LEVEL limits, not Twilio requirements. They serve to:
 * 1. **Prevent abuse/spam** - Stop tenants from mass-messaging
 * 2. **Control costs** - Each SMS costs money; limits prevent runaway charges
 * 3. **Protect sender reputation** - High-volume sending without proper 10DLC
 *    registration can trigger carrier filtering and hurt deliverability
 * 4. **Fair resource allocation** - Prevent one tenant from monopolizing capacity
 *
 * ## Twilio's Actual Limits
 *
 * Twilio has its own throughput limits that depend on:
 * - **Number type**: Long codes (1 SMS/sec), Toll-free (3 SMS/sec), Short codes (100+ SMS/sec)
 * - **10DLC registration**: Unregistered numbers are heavily filtered by carriers
 * - **A2P campaign status**: Registered campaigns get higher throughput
 * - **Account trust score**: New accounts have lower limits
 *
 * Our application limits should stay well below Twilio's limits to avoid
 * hitting their rate limits and triggering 429 errors.
 *
 * ## Preset Rationale
 *
 * - **Conservative (10/hr, 50/day)**: New tenants, low trust, or those with
 *   deliverability issues. Safe default to prevent problems.
 * - **Standard (50/hr, 200/day)**: Typical business usage. Allows reasonable
 *   follow-up messaging without risk of spam complaints.
 * - **High Volume (100/hr, 500/day)**: Established tenants with good standing
 *   and proper 10DLC registration.
 * - **Very High (500/hr, 2000/day)**: Enterprise tenants with verified campaigns
 *   and dedicated support.
 *
 * ## Features
 * - Lists all tenants with their SMS rate limits and current usage
 * - Inline editing of rate limits per tenant
 * - Bulk update limits for selected tenants
 * - Preset configurations (Conservative, Standard, High Volume)
 * - Reset usage counters per tenant
 * - Responsive table with mobile card view
 */
import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { MainLayout } from '@/components/layout/MainLayout';
import {
  useAdminTenantSMSLimits,
  useAdminUpdateSMSLimits,
  useAdminBulkUpdateSMSLimits,
  useAdminClearRateLimits,
  TenantSMSLimits,
} from '@/hooks/useAdminSMSLimits';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Clock, RefreshCw, Save, Settings2, Zap, Info, ChevronDown, ChevronUp } from 'lucide-react';

interface PresetConfig {
  label: string;
  hourly: number;
  daily: number;
  description: string;
}

const PRESETS: Record<string, PresetConfig> = {
  conservative: {
    label: 'Conservative',
    hourly: 10,
    daily: 50,
    description: 'Low volume - prevents spam',
  },
  standard: {
    label: 'Standard',
    hourly: 50,
    daily: 200,
    description: 'Balanced for most tenants',
  },
  high: {
    label: 'High Volume',
    hourly: 100,
    daily: 500,
    description: 'For high-traffic tenants',
  },
  unlimited: {
    label: 'Very High',
    hourly: 500,
    daily: 2000,
    description: 'Maximum throughput',
  },
};

export default function SMSRateLimitsPage() {
  const { isAdmin, loading: authLoading } = useAuth();
  const { data: tenants, isLoading, error } = useAdminTenantSMSLimits();
  const updateMutation = useAdminUpdateSMSLimits();
  const bulkUpdateMutation = useAdminBulkUpdateSMSLimits();
  const clearRateLimitsMutation = useAdminClearRateLimits();

  const [selectedTenants, setSelectedTenants] = useState<Set<string>>(new Set());
  const [editingTenant, setEditingTenant] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ hourly: number; daily: number }>({
    hourly: 50,
    daily: 200,
  });
  const [bulkPreset, setBulkPreset] = useState<string>('');
  const [confirmReset, setConfirmReset] = useState<{ open: boolean; tenantId: string; tenantName: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showInfo, setShowInfo] = useState(false);

  // Redirect non-admins
  if (!authLoading && !isAdmin?.()) {
    return <Navigate to="/" replace />;
  }

  const filteredTenants = (tenants || []).filter((t) =>
    t.out_tenant_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedTenants(new Set(filteredTenants.map((t) => t.out_tenant_id)));
    } else {
      setSelectedTenants(new Set());
    }
  };

  const handleSelectTenant = (tenantId: string, checked: boolean) => {
    const newSelection = new Set(selectedTenants);
    if (checked) {
      newSelection.add(tenantId);
    } else {
      newSelection.delete(tenantId);
    }
    setSelectedTenants(newSelection);
  };

  const handleStartEdit = (tenant: TenantSMSLimits) => {
    setEditingTenant(tenant.out_tenant_id);
    setEditValues({
      hourly: tenant.out_sms_rate_limit_per_hour,
      daily: tenant.out_sms_rate_limit_per_day,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingTenant) return;

    try {
      await updateMutation.mutateAsync({
        tenantId: editingTenant,
        hourlyLimit: editValues.hourly,
        dailyLimit: editValues.daily,
      });
      toast.success('Rate limits updated');
      setEditingTenant(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update');
    }
  };

  const handleCancelEdit = () => {
    setEditingTenant(null);
  };

  const handleBulkUpdate = async () => {
    if (selectedTenants.size === 0 || !bulkPreset) {
      toast.error('Select tenants and a preset first');
      return;
    }

    const preset = PRESETS[bulkPreset];
    if (!preset) return;

    try {
      const count = await bulkUpdateMutation.mutateAsync({
        tenantIds: Array.from(selectedTenants),
        hourlyLimit: preset.hourly,
        dailyLimit: preset.daily,
      });
      toast.success(`Updated ${count} tenant(s) to ${preset.label}`);
      setSelectedTenants(new Set());
      setBulkPreset('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to bulk update');
    }
  };

  const handleResetUsage = async () => {
    if (!confirmReset) return;

    try {
      await clearRateLimitsMutation.mutateAsync(confirmReset.tenantId);
      toast.success(`Usage counters cleared for ${confirmReset.tenantName}`);
      setConfirmReset(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to clear usage');
    }
  };

  const getUsageBadge = (current: number, limit: number) => {
    const percentage = limit > 0 ? (current / limit) * 100 : 0;
    if (percentage >= 90) {
      return <Badge variant="destructive">{current}/{limit}</Badge>;
    } else if (percentage >= 70) {
      return <Badge variant="secondary" className="bg-amber-100 text-amber-800">{current}/{limit}</Badge>;
    }
    return <Badge variant="outline">{current}/{limit}</Badge>;
  };

  if (authLoading || isLoading) {
    return (
      <MainLayout>
        <div className="container mx-auto py-6 space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="container mx-auto py-6">
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive">Error Loading Data</CardTitle>
              <CardDescription>{error.message}</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Clock className="h-6 w-6" />
              SMS Rate Limits
            </h1>
            <p className="text-muted-foreground">
              Manage SMS sending limits across all tenants
            </p>
          </div>

          {/* Bulk Actions */}
          <div className="flex flex-wrap items-center gap-2">
            <Select value={bulkPreset} onValueChange={setBulkPreset}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Select preset..." />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PRESETS).map(([key, preset]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex flex-col">
                      <span>{preset.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {preset.hourly}/hr, {preset.daily}/day
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={handleBulkUpdate}
              disabled={selectedTenants.size === 0 || !bulkPreset || bulkUpdateMutation.isPending}
            >
              <Zap className="h-4 w-4 mr-1" />
              Apply to {selectedTenants.size > 0 ? `${selectedTenants.size} Selected` : 'Selected'}
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="flex items-center gap-4">
          <Input
            placeholder="Search tenants..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-sm"
          />
          <span className="text-sm text-muted-foreground">
            {filteredTenants.length} tenant(s)
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowInfo(!showInfo)}
            className="ml-auto"
          >
            <Info className="h-4 w-4 mr-1" />
            About Limits
            {showInfo ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
          </Button>
        </div>

        {/* Info Card */}
        {showInfo && (
          <Card className="bg-muted/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="h-4 w-4" />
                About SMS Rate Limits
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-4">
              <div>
                <h4 className="font-medium mb-1">Why These Limits Exist</h4>
                <p className="text-muted-foreground">
                  These are <strong>application-level</strong> limits (not Twilio requirements) that help:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mt-1 space-y-1">
                  <li>Prevent abuse and spam from tenants</li>
                  <li>Control SMS costs (each message costs money)</li>
                  <li>Protect sender reputation and deliverability</li>
                  <li>Ensure fair resource allocation across tenants</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium mb-1">Twilio's Actual Limits</h4>
                <p className="text-muted-foreground">
                  Twilio has its own limits based on number type, 10DLC registration, and account trust.
                  Long codes can send ~1 SMS/sec, but carriers filter unregistered high-volume traffic.
                  Our limits should stay below Twilio's to avoid 429 errors.
                </p>
              </div>

              <div>
                <h4 className="font-medium mb-2">Preset Guidelines</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="p-2 bg-background rounded border">
                    <span className="font-medium">Conservative</span>
                    <span className="text-muted-foreground text-xs ml-2">(10/hr, 50/day)</span>
                    <p className="text-xs text-muted-foreground">New tenants or those with deliverability issues</p>
                  </div>
                  <div className="p-2 bg-background rounded border">
                    <span className="font-medium">Standard</span>
                    <span className="text-muted-foreground text-xs ml-2">(50/hr, 200/day)</span>
                    <p className="text-xs text-muted-foreground">Typical business usage, good default</p>
                  </div>
                  <div className="p-2 bg-background rounded border">
                    <span className="font-medium">High Volume</span>
                    <span className="text-muted-foreground text-xs ml-2">(100/hr, 500/day)</span>
                    <p className="text-xs text-muted-foreground">Established tenants with 10DLC registration</p>
                  </div>
                  <div className="p-2 bg-background rounded border">
                    <span className="font-medium">Very High</span>
                    <span className="text-muted-foreground text-xs ml-2">(500/hr, 2000/day)</span>
                    <p className="text-xs text-muted-foreground">Enterprise with verified campaigns</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Desktop Table View */}
        <Card className="hidden md:block">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={
                        filteredTenants.length > 0 &&
                        filteredTenants.every((t) => selectedTenants.has(t.out_tenant_id))
                      }
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Tenant</TableHead>
                  <TableHead className="text-center">Hourly Limit</TableHead>
                  <TableHead className="text-center">Daily Limit</TableHead>
                  <TableHead className="text-center">Hourly Usage</TableHead>
                  <TableHead className="text-center">Daily Usage</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTenants.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No tenants found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTenants.map((tenant) => (
                    <TableRow key={tenant.out_tenant_id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedTenants.has(tenant.out_tenant_id)}
                          onCheckedChange={(checked) =>
                            handleSelectTenant(tenant.out_tenant_id, checked as boolean)
                          }
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {tenant.out_tenant_name || 'Unnamed Tenant'}
                      </TableCell>
                      <TableCell className="text-center">
                        {editingTenant === tenant.out_tenant_id ? (
                          <Input
                            type="number"
                            min={1}
                            max={1000}
                            value={editValues.hourly}
                            onChange={(e) =>
                              setEditValues({ ...editValues, hourly: parseInt(e.target.value) || 1 })
                            }
                            className="w-20 mx-auto"
                          />
                        ) : (
                          tenant.out_sms_rate_limit_per_hour
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {editingTenant === tenant.out_tenant_id ? (
                          <Input
                            type="number"
                            min={1}
                            max={10000}
                            value={editValues.daily}
                            onChange={(e) =>
                              setEditValues({ ...editValues, daily: parseInt(e.target.value) || 1 })
                            }
                            className="w-20 mx-auto"
                          />
                        ) : (
                          tenant.out_sms_rate_limit_per_day
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {getUsageBadge(
                          tenant.out_current_hour_usage,
                          tenant.out_sms_rate_limit_per_hour
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {getUsageBadge(
                          tenant.out_current_day_usage,
                          tenant.out_sms_rate_limit_per_day
                        )}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        {editingTenant === tenant.out_tenant_id ? (
                          <>
                            <Button
                              size="sm"
                              onClick={handleSaveEdit}
                              disabled={updateMutation.isPending}
                            >
                              <Save className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStartEdit(tenant)}
                            >
                              <Settings2 className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                setConfirmReset({
                                  open: true,
                                  tenantId: tenant.out_tenant_id,
                                  tenantName: tenant.out_tenant_name || 'this tenant',
                                })
                              }
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-4">
          {filteredTenants.map((tenant) => (
            <Card key={tenant.out_tenant_id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={selectedTenants.has(tenant.out_tenant_id)}
                      onCheckedChange={(checked) =>
                        handleSelectTenant(tenant.out_tenant_id, checked as boolean)
                      }
                    />
                    <CardTitle className="text-base">
                      {tenant.out_tenant_name || 'Unnamed Tenant'}
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Hourly:</span>{' '}
                    <span className="font-medium">{tenant.out_sms_rate_limit_per_hour}</span>
                    <div className="mt-1">
                      {getUsageBadge(
                        tenant.out_current_hour_usage,
                        tenant.out_sms_rate_limit_per_hour
                      )}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Daily:</span>{' '}
                    <span className="font-medium">{tenant.out_sms_rate_limit_per_day}</span>
                    <div className="mt-1">
                      {getUsageBadge(
                        tenant.out_current_day_usage,
                        tenant.out_sms_rate_limit_per_day
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => handleStartEdit(tenant)}
                  >
                    <Settings2 className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setConfirmReset({
                        open: true,
                        tenantId: tenant.out_tenant_id,
                        tenantName: tenant.out_tenant_name || 'this tenant',
                      })
                    }
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Confirm Reset Dialog */}
        <AlertDialog open={confirmReset?.open} onOpenChange={(open) => !open && setConfirmReset(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset Usage Counters?</AlertDialogTitle>
              <AlertDialogDescription>
                This will clear all rate limit usage counters for{' '}
                <strong>{confirmReset?.tenantName}</strong>. The tenant will be able to send messages
                again if they were rate-limited.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleResetUsage}
                disabled={clearRateLimitsMutation.isPending}
              >
                {clearRateLimitsMutation.isPending ? 'Resetting...' : 'Reset Counters'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
}
